import React from 'react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import StaticImageSlideshow from './staticimageslideshow';
import { Shuffle, Check, Palette, Share2 } from 'lucide-react';

// Modal that lets the user pin (or unpin) the style used for webhook-
// triggered map renders. The Random tile clears the pin on the user
// document; picking from the slideshow saves the underlying settings dict
// so the choice survives renames or deletions of the source style.
const WebhookStylePicker = ({ open, onOpenChange, currentLabel, onSave, onClear }) => {
  const isRandom = !currentLabel;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:rounded-2xl p-0 overflow-hidden max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <Palette className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold">Webhook map style</DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              Pick a style for auto-generated maps, or let the system choose a random one each time.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-4">
          <button
            type="button"
            onClick={() => { onClear(); onOpenChange(false); }}
            className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition
              ${isRandom ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <div className="h-10 w-10 rounded-full bg-gray-900 text-white flex items-center justify-center shrink-0">
              <Shuffle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">Random (any style)</div>
              <div className="text-xs text-gray-500">
                Each webhook render picks a fresh style from the pool.
              </div>
            </div>
            {isRandom && <Check className="h-4 w-4 text-blue-600" />}
          </button>

          {!isRandom && (
            <div className="text-xs text-gray-600">
              Current pick: <span className="font-medium text-gray-900">{currentLabel}</span>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 p-1">
            <StaticImageSlideshow
              onSettingsChange={(settings, name) => {
                onSave(settings, name || '');
                onOpenChange(false);
              }}
            />
          </div>

          {/* Hint: own styles only show up here once they've been shared
              from an unlocked map (Your Maps → share icon). */}
          <div className="flex items-start gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-600">
            <Share2 className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
            <p>
              Want to use one of your own styles here? Open it in
              <span className="font-medium"> Your Maps</span>, hit the share icon
              on any unlocked map, and it'll appear in this list.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookStylePicker;
